import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 26  -  Statistics & Fit Methods
// ============================================

/** 26.1 – Probability Density Functions */
export const PDFDistributions = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Probability Density Functions in HEP", W / 2, 24);

    // Three common PDFs
    const pdfs = [
      { name: "Gaussian", formula: "f(x) = exp(−(x−μ)²/2σ²) / (σ√2π)", color: "#3B82F6", use: "Measurement errors, resolution" },
      { name: "Poisson", formula: "P(k) = λᵏ e⁻λ / k!", color: "#8B5CF6", use: "Counting experiments, rare events" },
      { name: "Breit-Wigner", formula: "f(E) ∝ 1/((E−M)²+(Γ/2)²)", color: "#EF4444", use: "Resonance mass peaks (Z, W, H)" },
    ];

    pdfs.forEach((p, i) => {
      const x = 15 + i * 182, y = 45, w = 172, h = 80;
      ctx.fillStyle = p.color + "12";
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = p.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = p.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.fillText(p.name, x + w / 2, y + 20);
      ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
      ctx.fillText(p.formula, x + w / 2, y + 40);
      ctx.fillStyle = "#64748B"; ctx.font = "10px Inter,sans-serif";
      ctx.fillText(p.use, x + w / 2, y + 60);
    });

    // Draw each PDF shape
    const plotY = 140, plotH = 100, plotW = 150;
    pdfs.forEach((p, i) => {
      const plotX = 30 + i * 182;
      ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(plotX, plotY + plotH); ctx.lineTo(plotX + plotW, plotY + plotH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(plotX, plotY + plotH); ctx.lineTo(plotX, plotY); ctx.stroke();
      ctx.strokeStyle = p.color; ctx.lineWidth = 2;
      ctx.beginPath();
      for (let j = 0; j <= plotW; j++) {
        const t = (j / plotW - 0.5) * 6;
        let val;
        if (i === 0) val = Math.exp(-t * t / 2) / Math.sqrt(2 * Math.PI);
        else if (i === 1) { const k = Math.max(0, Math.round((t + 3) * 1.5)); const lam = 4; val = Math.pow(lam, k) * Math.exp(-lam) / (function(n){let r=1;for(let x=2;x<=n;x++)r*=x;return r;})(k) * 2; }
        else val = 1 / ((t * t) + 1) / Math.PI;
        const y = plotY + plotH - val * plotH * 2.5;
        j === 0 ? ctx.moveTo(plotX + j, y) : ctx.lineTo(plotX + j, y);
      }
      ctx.stroke();
    });

    // Key concepts
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Key Properties", W / 2, 265);
    const props = [
      "Normalization: ∫ f(x) dx = 1  (total probability = 1)",
      "Mean: μ = ∫ x·f(x) dx    Variance: σ² = ∫ (x−μ)²·f(x) dx",
      "Central Limit Theorem: sum of many variables → Gaussian",
      "In HEP: fit signal PDF + background PDF to data histogram",
    ];
    ctx.font = "12px Inter,sans-serif"; ctx.fillStyle = "#475569";
    props.forEach((p, i) => ctx.fillText(p, W / 2, 285 + i * 20));
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 26.2 – Maximum Likelihood Estimation */
export const MaxLikelihood = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Maximum Likelihood Estimation (MLE)", W / 2, 24);

    // Main formula
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(40, 45, 480, 55, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(40, 45, 480, 55, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 14px Inter,sans-serif";
    ctx.fillText("L(θ) = ∏ᵢ f(xᵢ | θ)     →   maximize ln L(θ)", W / 2, 68);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Product of PDF values for each observed data point xᵢ", W / 2, 90);

    // Step-by-step
    const steps = [
      { step: "1", text: "Choose model f(x|θ) with parameters θ", color: "#3B82F6" },
      { step: "2", text: "Compute L(θ) = ∏ f(xᵢ|θ) for your data", color: "#8B5CF6" },
      { step: "3", text: "Take log: ln L = Σ ln f(xᵢ|θ)  (easier to compute)", color: "#10B981" },
      { step: "4", text: "Differentiate: ∂(ln L)/∂θ = 0  → solve for θ̂", color: "#F59E0B" },
      { step: "5", text: "Error: σ_θ from −∂²(ln L)/∂θ² (curvature at maximum)", color: "#EF4444" },
    ];
    steps.forEach((s, i) => {
      const y = 115 + i * 28;
      ctx.fillStyle = s.color; ctx.font = "bold 14px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(s.step, 40, y);
      ctx.fillStyle = "#1E293B"; ctx.font = "12px Inter,sans-serif";
      ctx.fillText(s.text, 65, y);
    });
    ctx.textAlign = "center";

    // Log-likelihood curve
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Log-Likelihood Curve", W / 2, 270);
    const pL = 120, pR = 440, pT = 285, pB = 340;
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pR, pB); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pL, pT); ctx.stroke();
    ctx.fillStyle = "#64748B"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("θ", (pL + pR) / 2, pB + 14);
    ctx.save(); ctx.translate(pL - 12, (pT + pB) / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillText("ln L", 0, 0); ctx.restore();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 100; i++) {
      const t = (i / 100 - 0.5) * 4;
      const y = pT + 5 + (pB - pT - 10) * (1 - Math.exp(-t * t / 2));
      const x = pL + (i / 100) * (pR - pL);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    // Peak marker
    ctx.fillStyle = "#EF4444";
    ctx.beginPath(); ctx.arc((pL + pR) / 2, pT + 5, 4, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#EF4444"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("θ̂ (MLE)", (pL + pR) / 2, pT - 2);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 26.3 – Chi-Squared Test and Goodness of Fit */
export const ChiSquaredTest = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Chi-Squared (χ²) Goodness of Fit", W / 2, 24);

    // Formula
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(60, 45, 440, 50, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(60, 45, 440, 50, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.fillText("χ² = Σᵢ (Oᵢ − Eᵢ)² / Eᵢ", W / 2, 70);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("O = observed counts,  E = expected counts,  sum over bins", W / 2, 88);

    // Interpretation
    const interp = [
      { range: "χ²/ndf ≈ 1", meaning: "Good fit  -  data matches model", color: "#10B981" },
      { range: "χ²/ndf ≫ 1", meaning: "Poor fit  -  model doesn't describe data", color: "#EF4444" },
      { range: "χ²/ndf ≪ 1", meaning: "Suspiciously good  -  errors overestimated?", color: "#F59E0B" },
    ];
    interp.forEach((it, i) => {
      const y = 115 + i * 30;
      ctx.fillStyle = it.color + "15";
      ctx.beginPath(); ctx.roundRect(40, y, 480, 24, 6); ctx.fill();
      ctx.strokeStyle = it.color; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.roundRect(40, y, 480, 24, 6); ctx.stroke();
      ctx.fillStyle = it.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(it.range, 55, y + 17);
      ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
      ctx.fillText(it.meaning, 210, y + 17);
    });
    ctx.textAlign = "center";

    // χ² distribution plot
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("χ² Distribution (different ndf)", W / 2, 225);
    const pL = 80, pR = 480, pT = 240, pB = 325;
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pR, pB); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pL, pT); ctx.stroke();
    ctx.fillStyle = "#64748B"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("χ²", (pL + pR) / 2, pB + 14);
    const ndfs = [{ ndf: 2, color: "#3B82F6" }, { ndf: 5, color: "#8B5CF6" }, { ndf: 10, color: "#10B981" }];
    ndfs.forEach((n) => {
      ctx.strokeStyle = n.color; ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 1; i <= 200; i++) {
        const x2 = (i / 200) * 25;
        const k = n.ndf / 2;
        const val = Math.pow(x2, k - 1) * Math.exp(-x2 / 2) / (Math.pow(2, k) * 2);
        const px = pL + (i / 200) * (pR - pL);
        const py = pB - Math.min(val * (pB - pT) * 8, pB - pT);
        i === 1 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.stroke();
    });
    // Legend
    ndfs.forEach((n, i) => {
      ctx.fillStyle = n.color; ctx.font = "bold 10px Inter,sans-serif";
      ctx.fillText(`ndf=${n.ndf}`, pR - 70, pT + 10 + i * 14);
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 26.4 – Hypothesis Testing in Particle Physics */
export const HypothesisTesting = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Hypothesis Testing in Particle Physics", W / 2, 24);

    // H0 vs H1
    const hyps = [
      { label: "H₀ (Null)", desc: "Background only\n(no new physics)", color: "#3B82F6", bg: "#DBEAFE" },
      { label: "H₁ (Signal)", desc: "Signal + Background\n(new particle exists)", color: "#EF4444", bg: "#FEE2E2" },
    ];
    hyps.forEach((h, i) => {
      const x = 40 + i * 270, y = 45, w = 210, h2 = 70;
      ctx.fillStyle = h.bg;
      ctx.beginPath(); ctx.roundRect(x, y, w, h2, 10); ctx.fill();
      ctx.strokeStyle = h.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(x, y, w, h2, 10); ctx.stroke();
      ctx.fillStyle = h.color; ctx.font = "bold 14px Inter,sans-serif";
      ctx.fillText(h.label, x + w / 2, y + 22);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      h.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 42 + li * 14));
    });

    // Test statistic distributions
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Test Statistic Distribution Under H₀ and H₁", W / 2, 140);
    const pL = 80, pR = 480, pT = 155, pB = 260;
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pR, pB); ctx.stroke();
    // H0 distribution
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 200; i++) {
      const t = (i / 200) * 10 - 2;
      const val = Math.exp(-(t - 2) * (t - 2) / 2) / Math.sqrt(2 * Math.PI);
      const x = pL + (i / 200) * (pR - pL);
      const y = pB - val * (pB - pT) * 2.5;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    // H1 distribution
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 200; i++) {
      const t = (i / 200) * 10 - 2;
      const val = Math.exp(-(t - 5) * (t - 5) / 2) / Math.sqrt(2 * Math.PI);
      const x = pL + (i / 200) * (pR - pL);
      const y = pB - val * (pB - pT) * 2.5;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    // p-value shading
    ctx.fillStyle = "#3B82F620";
    ctx.beginPath(); ctx.moveTo(pL + 140, pB);
    for (let i = 70; i <= 200; i++) {
      const t = (i / 200) * 10 - 2;
      const val = Math.exp(-(t - 2) * (t - 2) / 2) / Math.sqrt(2 * Math.PI);
      ctx.lineTo(pL + (i / 200) * (pR - pL), pB - val * (pB - pT) * 2.5);
    }
    ctx.lineTo(pR, pB); ctx.closePath(); ctx.fill();
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("H₀ (bg only)", pL + 80, pT + 15);
    ctx.fillStyle = "#EF4444";
    ctx.fillText("H₁ (sig+bg)", pR - 60, pT + 15);
    ctx.fillStyle = "#3B82F6"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("p-value", pL + 160, pB - 10);

    // Key concepts
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Key Concepts", W / 2, 280);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("p-value: probability of data (or more extreme) under H₀", W / 2, 298);
    ctx.fillText("Significance: Z = Φ⁻¹(1−p)  in units of σ (standard deviations)", W / 2, 316);
    ctx.fillText("Discovery: p < 2.87�-10⁻⁷ → Z > 5σ → claim discovery", W / 2, 334);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Exclusion: p < 0.05 → 95% CL exclusion of signal hypothesis", W / 2, 352);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 26.5 – The CLs Method */
export const CLsMethod = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("The CLs Method  -  Modified Frequentist", W / 2, 24);

    // Formula
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(60, 45, 440, 55, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(60, 45, 440, 55, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.fillText("CLs = CLs+b / CLb = p(s+b) / (1 − p(b))", W / 2, 68);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Protects against excluding signal when sensitivity is low", W / 2, 90);

    // Problem with naive p-value
    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(30, 115, 240, 80, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 115, 240, 80, 10); ctx.stroke();
    ctx.fillStyle = "#991B1B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Problem: naive CLs+b", 150, 135);
    ctx.fillStyle = "#EF4444"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Can exclude signal even when", 150, 155);
    ctx.fillText("experiment has no sensitivity", 150, 170);
    ctx.fillText("(downward bg fluctuation)", 150, 185);

    // Solution: CLs
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(290, 115, 240, 80, 10); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 115, 240, 80, 10); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Solution: CLs ratio", 410, 135);
    ctx.fillStyle = "#10B981"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Dividing by CLb penalizes", 410, 155);
    ctx.fillText("exclusions from bg fluctuations", 410, 170);
    ctx.fillText("Conservative but robust", 410, 185);

    // Exclusion plot sketch
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("CLs Exclusion Limit", W / 2, 215);
    const pL = 80, pR = 480, pT = 230, pB = 320;
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pR, pB); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pL, pB); ctx.lineTo(pL, pT); ctx.stroke();
    ctx.fillStyle = "#64748B"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Signal strength μ", (pL + pR) / 2, pB + 14);
    ctx.save(); ctx.translate(pL - 15, (pT + pB) / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillText("CLs", 0, 0); ctx.restore();
    // CLs curve
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 200; i++) {
      const mu = (i / 200) * 3;
      const cls = 1 / (1 + mu * mu * 2);
      const x = pL + (i / 200) * (pR - pL);
      const y = pT + (1 - cls) * (pB - pT);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    // 0.05 line
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 1; ctx.setLineDash([5, 4]);
    const y05 = pT + (1 - 0.05) * (pB - pT);
    ctx.beginPath(); ctx.moveTo(pL, y05); ctx.lineTo(pR, y05); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#EF4444"; ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "right"; ctx.fillText("CLs = 0.05 (95% CL)", pR, y05 - 5);
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Standard method at LEP, Tevatron, LHC for Higgs and BSM searches", W / 2, 350);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 26.6 – Systematic Uncertainties */
export const SystematicUncertainties = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Systematic Uncertainties", W / 2, 24);

    // Stat vs Systematic
    const types = [
      { title: "Statistical", color: "#3B82F6", bg: "#DBEAFE",
        items: ["Random fluctuations", "Decrease with more data: ∝ 1/√N", "Easy to estimate", "Reducible with larger sample"] },
      { title: "Systematic", color: "#EF4444", bg: "#FEE2E2",
        items: ["Bias in measurement method", "Do NOT decrease with more data", "Hard to estimate  -  need studies", "Often dominate at LHC precision"] },
    ];
    types.forEach((t, i) => {
      const x = 25 + i * 270, y = 45, w = 240, h = 120;
      ctx.fillStyle = t.bg;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = t.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = t.color; ctx.font = "bold 13px Inter,sans-serif";
      ctx.fillText(t.title, x + w / 2, y + 22);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      t.items.forEach((it, ii) => ctx.fillText(it, x + w / 2, y + 42 + ii * 18));
    });

    // Common systematics in HEP
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Common Sources of Systematic Uncertainty", W / 2, 190);
    const sources = [
      { src: "Energy scale", desc: "Calorimeter calibration uncertainty (1–3%)", color: "#3B82F6" },
      { src: "Trigger efficiency", desc: "Fraction of events passing trigger (data vs MC)", color: "#8B5CF6" },
      { src: "Luminosity", desc: "Uncertainty on integrated luminosity (1–2% at LHC)", color: "#10B981" },
      { src: "Background model", desc: "Choice of background PDF shape, normalization", color: "#F59E0B" },
      { src: "Pile-up modelling", desc: "Simulated vs actual number of interactions", color: "#EF4444" },
      { src: "Theory", desc: "PDF uncertainties, higher-order corrections, scale choices", color: "#64748B" },
    ];
    sources.forEach((s, i) => {
      const y = 205 + i * 22;
      ctx.fillStyle = s.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(s.src, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(s.desc, 180, y);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Propagate via nuisance parameters in likelihood: L(μ, θ) with profiling", W / 2, 350);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 26.7 – Higgs Boson Discovery Statistics */
export const HiggsDiscoveryStats = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Higgs Boson Discovery  -  Statistical Significance", W / 2, 24);

    // Timeline
    const milestones = [
      { date: "Dec 2011", event: "2.5σ hints at ~126 GeV", color: "#F59E0B" },
      { date: "Jul 4, 2012", event: "5.0σ discovery announced!", color: "#10B981" },
      { date: "Combined", event: "ATLAS 5.9σ + CMS 5.0σ", color: "#3B82F6" },
    ];
    milestones.forEach((m, i) => {
      const x = 30 + i * 180, y = 50, w = 165, h = 55;
      ctx.fillStyle = m.color + "15";
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.fill();
      ctx.strokeStyle = m.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.stroke();
      ctx.fillStyle = m.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.fillText(m.date, x + w / 2, y + 18);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(m.event, x + w / 2, y + 38);
    });

    // Key channels
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Discovery Channels", W / 2, 130);
    const channels = [
      { ch: "H → γγ", sig: "Narrow mass peak over smooth bg", sigma: "4.1σ (CMS), 4.5σ (ATLAS)", color: "#3B82F6" },
      { ch: "H → ZZ* → 4ℓ", sig: "4 leptons  -  very clean", sigma: "3.2σ (CMS), 3.6σ (ATLAS)", color: "#8B5CF6" },
      { ch: "H → WW* → ℓνℓν", sig: "Excess in events (no mass peak)", sigma: "Supportive evidence", color: "#10B981" },
    ];
    channels.forEach((ch, i) => {
      const y = 145 + i * 28;
      ctx.fillStyle = ch.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(ch.ch, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(ch.sig, 160, y);
      ctx.fillStyle = ch.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "right"; ctx.fillText(ch.sigma, 540, y);
    });
    ctx.textAlign = "center";

    // p-value explanation
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath(); ctx.roundRect(40, 230, 480, 60, 10); ctx.fill();
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(40, 230, 480, 60, 10); ctx.stroke();
    ctx.fillStyle = "#92400E"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("5σ = p-value of 2.87 �- 10⁻⁷", W / 2, 252);
    ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Less than 1 in 3.5 million chance of background-only explanation", W / 2, 272);
    ctx.fillText("Standard threshold in particle physics since LEP era", W / 2, 285);

    // Sigma table
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Sigma Scale", W / 2, 310);
    const sigmas = [
      { s: "1σ", p: "0.317", meaning: "32%  -  no evidence" },
      { s: "2σ", p: "0.046", meaning: "4.6%  -  interesting" },
      { s: "3σ", p: "0.0027", meaning: "0.27%  -  evidence" },
      { s: "5σ", p: "2.87�-10⁻⁷", meaning: "Discovery!" },
    ];
    ctx.font = "11px Inter,sans-serif";
    sigmas.forEach((sg, i) => {
      const x = 60 + i * 130;
      ctx.fillStyle = i === 3 ? "#10B981" : "#475569";
      ctx.font = i === 3 ? "bold 11px Inter,sans-serif" : "11px Inter,sans-serif";
      ctx.fillText(`${sg.s}: p=${sg.p}`, x, 330);
      ctx.fillText(sg.meaning, x, 345);
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

// ============================================
// Chapter 27  -  Beamline Instrumentation
// (27.1 already has BeamlineSchematic in PhysicsInfographics)
// ============================================

/** 27.2 – Wire Chambers and MWPCs */
export const WireChamberMWPC = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Multi-Wire Proportional Chamber (MWPC)", W / 2, 24);

    // Cross-section diagram
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 50, 300, 160, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 50, 300, 160, 10); ctx.stroke();
    // Cathode planes (top & bottom)
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(50, 75); ctx.lineTo(310, 75); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(50, 185); ctx.lineTo(310, 185); ctx.stroke();
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Cathode plane (−HV)", 50, 68);
    ctx.fillText("Cathode plane (−HV)", 50, 200);
    // Anode wires
    for (let i = 0; i < 10; i++) {
      const wx = 70 + i * 25;
      ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(wx, 78); ctx.lineTo(wx, 182); ctx.stroke();
      ctx.fillStyle = "#F59E0B";
      ctx.beginPath(); ctx.arc(wx, 130, 2, 0, Math.PI * 2); ctx.fill();
    }
    ctx.fillStyle = "#F59E0B"; ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "center"; ctx.fillText("Anode wires (+HV, ~20 μm Ø)", 180, 145);
    // Gas fill
    ctx.fillStyle = "#3B82F640"; ctx.fillRect(50, 78, 260, 104);
    ctx.fillStyle = "#3B82F6"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Gas: Ar/CO₂ or Ar/isobutane", 180, 165);
    // Particle track
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(180, 45); ctx.lineTo(180, 215); ctx.stroke();
    ctx.fillStyle = "#EF4444"; ctx.font = "bold 10px Inter,sans-serif";
    ctx.fillText("particle", 200, 55);
    ctx.textAlign = "center";

    // Specs
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(350, 50, 190, 160, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(350, 50, 190, 160, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Key Properties", 445, 72);
    const specs = [
      "Wire spacing: 1–2 mm",
      "Position σ ~ 200 μm",
      "Time resolution ~ 50 ns",
      "Gas gain: 10⁴–10⁵",
      "Invented: Charpak 1968",
      "Nobel Prize 1992",
    ];
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    specs.forEach((s, i) => ctx.fillText(s, 445, 92 + i * 18));

    // How it works
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Principle", W / 2, 235);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("Charged particle ionises gas → electrons drift to nearest anode wire", W / 2, 255);
    ctx.fillText("Avalanche near wire (high E-field) → measurable signal on anode", W / 2, 275);
    ctx.fillText("Which wire fires → position; pulse height ∝ dE/dx → particle ID", W / 2, 295);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Used at BL4S for tracking: locate particle trajectory in x-y plane", W / 2, 325);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 27.3 – Beam Current Transformers */
export const BeamCurrentTransformer = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 320;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Beam Current Transformers (BCTs)", W / 2, 24);

    // Toroid diagram
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 50, 260, 140, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 50, 260, 140, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Toroidal Transformer", 160, 70);
    // Beam pipe (horizontal line through center)
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(50, 130); ctx.lineTo(280, 130); ctx.stroke();
    ctx.fillStyle = "#EF4444"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Beam", 55, 122);
    // Toroid ring
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(160, 130, 35, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = "#3B82F6"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Ferrite core +", 160, 178);
    ctx.fillText("pickup coil", 160, 190);

    // Principle
    ctx.fillStyle = "#EDE9FE";
    ctx.beginPath(); ctx.roundRect(310, 50, 230, 140, 10); ctx.fill();
    ctx.strokeStyle = "#8B5CF6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(310, 50, 230, 140, 10); ctx.stroke();
    ctx.fillStyle = "#6D28D9"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("How It Works", 425, 70);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    const howIt = [
      "Beam = primary winding (1 turn)",
      "Toroid core couples mag. flux",
      "Secondary coil measures V_ind",
      "V = −M · dI_beam/dt",
      "Non-destructive: no beam contact",
      "Sensitivity: ~1 μA resolution",
    ];
    howIt.forEach((h, i) => ctx.fillText(h, 425, 90 + i * 17));

    // Types
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("BCT Types at CERN", W / 2, 215);
    const bctTypes = [
      { type: "AC (fast) BCT", use: "Bunch-by-bunch intensity, ns resolution", color: "#3B82F6" },
      { type: "DC CT (DCCT)", use: "Average circulating current, high precision", color: "#8B5CF6" },
      { type: "Wall Current Monitor", use: "Broadband beam signal on resistive wall", color: "#10B981" },
    ];
    bctTypes.forEach((b, i) => {
      ctx.fillStyle = b.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(b.type, 60, 235 + i * 22);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(b.use, 220, 235 + i * 22);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Essential for beam intensity monitoring  -  tells you how many particles per spill", W / 2, 310);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 27.4 – Collimators and Beam Shaping */
export const CollimatorsBeamShaping = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 320;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Collimators & Beam Shaping", W / 2, 24);

    // Beam + collimator diagram
    // Wide beam entering
    ctx.fillStyle = "#3B82F620";
    ctx.beginPath(); ctx.moveTo(30, 100); ctx.lineTo(200, 120); ctx.lineTo(200, 180); ctx.lineTo(30, 200); ctx.closePath(); ctx.fill();
    // Collimator jaws
    ctx.fillStyle = "#94A3B8";
    ctx.fillRect(200, 60, 30, 75);
    ctx.fillRect(200, 165, 30, 75);
    ctx.fillStyle = "#64748B"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("Jaw", 215, 100);
    ctx.fillText("Jaw", 215, 200);
    // Narrow beam after
    ctx.fillStyle = "#3B82F640";
    ctx.beginPath(); ctx.moveTo(230, 135); ctx.lineTo(530, 140); ctx.lineTo(530, 160); ctx.lineTo(230, 165); ctx.closePath(); ctx.fill();
    // Labels
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Wide beam", 100, 90);
    ctx.fillText("Collimated beam", 400, 130);
    ctx.fillStyle = "#EF4444"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Halo particles", 100, 210);
    ctx.fillText("absorbed", 100, 222);

    // Types of collimators
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Collimator Functions", W / 2, 250);
    const funcs = [
      { func: "Primary (TCP)", desc: "Scrape beam halo  -  define aperture", color: "#3B82F6" },
      { func: "Secondary (TCS)", desc: "Catch scattered particles from TCP", color: "#8B5CF6" },
      { func: "Absorber (TCLA)", desc: "Final cleanup  -  protect downstream", color: "#EF4444" },
    ];
    funcs.forEach((f, i) => {
      ctx.fillStyle = f.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(f.func, 60, 270 + i * 18);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(f.desc, 200, 270 + i * 18);
    });
    ctx.textAlign = "center";
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 27.5 – Beam Dumps and Safety Systems */
export const BeamDumpSafety = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 320;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Beam Dumps & Safety Systems", W / 2, 24);

    // Beam dump diagram
    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(30, 50, 500, 100, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 50, 500, 100, 10); ctx.stroke();
    // Beam arrow
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(50, 100); ctx.lineTo(250, 100); ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.beginPath(); ctx.moveTo(250, 100); ctx.lineTo(243, 95); ctx.lineTo(243, 105); ctx.closePath(); ctx.fill();
    ctx.font = "bold 11px Inter,sans-serif"; ctx.fillText("Beam (362 MJ at LHC!)", 150, 90);
    // Kicker magnet
    ctx.fillStyle = "#F59E0B";
    ctx.beginPath(); ctx.roundRect(260, 80, 50, 40, 5); ctx.fill();
    ctx.fillStyle = "#92400E"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Kicker", 285, 103);
    // Dilution magnets
    ctx.fillStyle = "#3B82F6";
    ctx.beginPath(); ctx.roundRect(330, 80, 50, 40, 5); ctx.fill();
    ctx.fillStyle = "white"; ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Diluter", 355, 103);
    // Dump block
    ctx.fillStyle = "#94A3B8";
    ctx.beginPath(); ctx.roundRect(410, 65, 100, 70, 8); ctx.fill();
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("Graphite", 460, 95);
    ctx.fillText("dump block", 460, 110);
    ctx.fillText("(8 m long)", 460, 125);

    // Safety systems
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Beam Interlock System (BIS)", W / 2, 175);
    const safety = [
      { sys: "Beam Loss Monitors", desc: "~3600 ionisation chambers around LHC ring", color: "#EF4444" },
      { sys: "Beam Interlock", desc: "If ANY monitor triggers → beam dump in 3 turns (~270 μs)", color: "#F59E0B" },
      { sys: "Collimation", desc: "Multi-stage protection of superconducting magnets", color: "#3B82F6" },
      { sys: "Access system", desc: "Personnel safety: interlocked doors, radiation surveys", color: "#8B5CF6" },
    ];
    safety.forEach((s, i) => {
      ctx.fillStyle = s.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(s.sys, 40, 195 + i * 22);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(s.desc, 200, 195 + i * 22);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("LHC stored energy equivalent to a TGV train at 150 km/h  -  dump is critical safety", W / 2, 305);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 27.6 – The T9 Beamline at CERN */
export const T9Beamline = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("The T9 Beamline  -  BL4S Experimental Area", W / 2, 24);

    // Beamline schematic (horizontal flow)
    const elements = [
      { label: "PS target\n(T6)", w: 70, color: "#EF4444" },
      { label: "Bending\nmagnets", w: 70, color: "#3B82F6" },
      { label: "Quad\nfocusing", w: 70, color: "#8B5CF6" },
      { label: "Collimator", w: 60, color: "#94A3B8" },
      { label: "Cherenkov\ncounter", w: 70, color: "#10B981" },
      { label: "Experiment\n(YOU!)", w: 70, color: "#F59E0B" },
    ];
    let xPos = 20;
    elements.forEach((el, i) => {
      const y = 55, h = 65;
      ctx.fillStyle = el.color + "20";
      ctx.beginPath(); ctx.roundRect(xPos, y, el.w, h, 8); ctx.fill();
      ctx.strokeStyle = el.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(xPos, y, el.w, h, 8); ctx.stroke();
      ctx.fillStyle = el.color; ctx.font = "bold 9px Inter,sans-serif";
      el.label.split("\n").forEach((ln, li) => ctx.fillText(ln, xPos + el.w / 2, y + 22 + li * 12));
      if (i < elements.length - 1) {
        ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(xPos + el.w + 2, y + h / 2); ctx.lineTo(xPos + el.w + 10, y + h / 2); ctx.stroke();
      }
      xPos += el.w + 12;
    });

    // Beam properties
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(30, 135, 500, 80, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(30, 135, 500, 80, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("T9 Beam Properties", W / 2, 155);
    const props = [
      "Momentum: 0.5 – 10 GeV/c (selectable)",
      "Composition: mixed π, K, p, e, μ (momentum-dependent)",
      "Spill: slow extraction from PS, ~400 ms flat top",
      "Rate: few �- 10³ particles/spill (adjustable)",
    ];
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    props.forEach((p, i) => ctx.fillText(p, W / 2, 172 + i * 15));

    // What's available
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Available Instrumentation at T9", W / 2, 240);
    const instr = [
      { item: "Scintillator hodoscopes", purpose: "Trigger + timing (TOF start/stop)", color: "#3B82F6" },
      { item: "Wire chambers (MWPCs)", purpose: "Tracking (x,y position per plane)", color: "#8B5CF6" },
      { item: "Cherenkov counters", purpose: "PID: separate π/K/p by threshold", color: "#10B981" },
      { item: "Lead-glass calorimeter", purpose: "EM energy + electron ID", color: "#F59E0B" },
    ];
    instr.forEach((ins, i) => {
      ctx.fillStyle = ins.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(ins.item, 40, 260 + i * 20);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(ins.purpose, 230, 260 + i * 20);
    });
    ctx.textAlign = "center";
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 27.7 – Complete T9 Beamline Walk-Through */
export const T9WalkThrough = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 340;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("T9 Walk-Through: From PS to Your Detector", W / 2, 24);

    // Step-by-step journey
    const journey = [
      { step: "1", title: "PS Extraction", desc: "24 GeV protons hit Be target at T6\nSecondary beam created (π, K, p, e⁻)", color: "#EF4444" },
      { step: "2", title: "Momentum Selection", desc: "Dipole magnets bend beam: p/q = Bρ\nOnly desired momentum passes slit", color: "#3B82F6" },
      { step: "3", title: "Beam Focusing", desc: "Quadrupole doublets focus in x and y\nBeam spot ~ 2 cm at experiment", color: "#8B5CF6" },
      { step: "4", title: "Particle ID", desc: "Cherenkov: tag e vs π vs K vs p\nTOF: confirm mass from flight time", color: "#10B981" },
      { step: "5", title: "Your Experiment!", desc: "Detectors in T9 experimental area\nData recorded to CERN DAQ system", color: "#F59E0B" },
    ];
    journey.forEach((j, i) => {
      const y = 45 + i * 57;
      ctx.fillStyle = j.color + "10";
      ctx.beginPath(); ctx.roundRect(20, y, 520, 50, 8); ctx.fill();
      ctx.strokeStyle = j.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(20, y, 520, 50, 8); ctx.stroke();
      ctx.fillStyle = j.color; ctx.font = "bold 14px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(j.step, 32, y + 18);
      ctx.fillStyle = j.color; ctx.font = "bold 12px Inter,sans-serif";
      ctx.fillText(j.title, 55, y + 18);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      j.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, 55, y + 34 + li * 13));
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Total beamline length: ~40 m from target to experimental area", W / 2, 335);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};
